/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { initiatePasswordReset } from '../../../../services/auth/passwordReset';

import LogoRMF from '../../../../assets/images/fullLogo.png';
import HeroContent from '../../Contents/HeroContent/HeroContent';
import LanguageSelector from '../../components/LanguageSelector/LanguageSelector';
import ThirdPartyAuthButtons from '../../components/ThirdPartyAuthButtons/ThirdPartyAuthButtons';
import OrDivider from '../../components/OrDivider/OrDivider';
import { heroStackSx, formPanelSx } from './styles';
import { LAYOUT } from '../../styles/layout';
import { registerColors } from '../../styles/colors';


const ForgotPassword = () => {
  const { t } = useTranslation('passwordReset');
  const location = useLocation();

  const [email, setEmail] = useState((location.state as { email?: string })?.email ?? '');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setEmailError('');
    setErrorMessage('');

    if (!email.trim()) {
      setEmailError(t('ERROR_EMAIL_REQUIRED'));
      return;
    }

    setIsSubmitting(true);

    try {
      await initiatePasswordReset(email);
      setSubmitted(true);
    } catch (err: any) {
      const message =
        err?.response?.data?.errors?.request || '';
    
      if (message.toLowerCase().includes('third-party')) {
        setErrorMessage(t('ERROR_THIRD_PARTY_ACCOUNT'));
      } else {
        setErrorMessage(t('ERROR_GENERIC'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        overflow: 'hidden',
      }}
    >
      <Stack width="100%" height="100%">
        <Stack
          direction="row"
          height="100%"
          sx={{
            overflow: 'hidden',
            '@media (max-width:1023px)': { flexDirection: 'column' },
          }}
        >
          {/* HERO */}
          <Stack width={LAYOUT.heroWidth} height="100%" sx={heroStackSx}>
            <HeroContent />
          </Stack>

          {/* PASSWORD RESET FORM */}
          <Stack
            justifyContent="center"
            alignItems="center"
            width={LAYOUT.formWidth}
            sx={formPanelSx}
          >
            <LanguageSelector />

            <Box component="img" src={LogoRMF} width="300px" height="auto" sx={{ mb: 2 }} />

            {/* TODO: connect these to actual handlers when implementing social login for password reset */}
            <Box width="100%">
              <ThirdPartyAuthButtons
                googleButtonText={t('BUTTON_SIGN_UP_WITH_GOOGLE')}
                raiseFxButtonText={t('BUTTON_SIGN_UP_WITH_RAISEFX')}
              />
              <OrDivider text={t('TEXT_OR')} />
            </Box>

            {submitted ? (
              <Stack width="100%" spacing={2} mt={4} alignItems="center">
                <Stack spacing={0.5} width="90%" alignItems="center">
                  <Typography variant="h4" fontWeight={700} textAlign="center">
                    {t('TITLE_REQUEST_SENT')}
                  </Typography>
                  <Typography variant="body2" textAlign="center" sx={{ color: registerColors.textMuted }}>
                    {t('TEXT_LINK_SENT')}
                  </Typography>
                </Stack>
                <Stack width="90%" alignItems="center">
                  <Typography variant="body1" textAlign="center" sx={{ color: registerColors.textBody }}>
                    {t('TEXT_FOLLOW_INSTRUCTIONS')}
                  </Typography>
                </Stack>
              </Stack>
            ) : (
              <Stack width="100%" spacing={4} mt={4}>
                <Stack spacing={1} alignItems="center">
                  <Typography variant="h4" fontWeight={700} textAlign="center">
                    {t('TITLE_FORGOT_PASSWORD')}
                  </Typography>
                  <Stack width="90%" alignItems="center">
                    <Typography variant="body1" textAlign="center" sx={{ color: registerColors.textMuted }}>
                      {t('TEXT_DESCRIPTION')}
                    </Typography>
                  </Stack>
                </Stack>

                {errorMessage && (
                  <Alert severity="error">{errorMessage}</Alert>
                )}

                <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                  <Stack spacing={3}>
                    <TextField
                      id="email"
                      label={t('LABEL_EMAIL_ADDRESS')}
                      variant="outlined"
                      placeholder={t('PLACEHOLDER_EMAIL_ADDRESS')}
                      type="email"
                      fullWidth
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      error={Boolean(emailError)}
                      helperText={emailError || ''}
                    />

                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={isSubmitting}
                      sx={{ height: '45px' }}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={24} />
                      ) : (
                        t('BUTTON_SUBMIT')
                      )}
                    </Button>

                    <Typography
                      variant="body2"
                      textAlign="right"
                      component={Link}
                      to="/login"
                      sx={{
                        textDecoration: 'none',
                        color: registerColors.textLink,
                        alignSelf: 'flex-end',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {t('LINK_BACK_TO_LOGIN')}
                    </Typography>
                  </Stack>
                </form>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ForgotPassword;
